/**
 * Deal detail page (PRD §11.4 + §8 lifecycle).
 *
 * The form mirrors the server's PATCH semantics: we only send fields the user
 * has actually changed (Pydantic `model_fields_set`). Stage transitions to a
 * terminal value (Won/Lost) trigger the §8.5 server-side check that the
 * contact has an account; the UI displays the resulting 409 inline so the
 * rep knows what to fix.
 *
 * Reopen (§8.6) is "set stage back to a non-terminal value on a closed
 * deal". The button is just a stage radio; the server clears `closed_at`.
 *
 * Sales can edit basics (status / value / text fields) on deals they own.
 * Manager+ can re-assign and change account_id. Only Admin can soft-delete.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import {
  CheckCircle2,
  Loader2,
  Pencil,
  RotateCcw,
  Save,
  ShoppingCart,
  Trash2,
  XCircle,
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import { BackLink } from "../components/BackLink";
import { ChatterPanel } from "../components/chatter/ChatterPanel";
import { CustomFieldsPanel } from "../components/custom-fields/CustomFieldsPanel";
import {
  DealStagePill,
  DealStatusPill,
  DealValueChip,
  stageLabel,
  statusLabel,
  valueLabel,
} from "../components/deals/Pills";
import { DispatchesPanel } from "../components/erp/DispatchesPanel";
import { FollowUpsPanel } from "../components/followups/FollowUpsPanel";
import {
  ApiError,
  type Deal,
  type DealUpdate,
  accountsApi,
  contactsApi,
  dealsApi,
  isDealConflictDetail,
  usersApi,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { usePicklistOptions, usePicklistTerminalSet } from "../lib/picklists";

interface DealDetailSearch {
  /** Optional ``?note=<id>`` deep-link from the bell / email — handed
   * straight to the chatter panel which scrolls + highlights it. */
  note?: string;
}

export const Route = createFileRoute("/_app/deals/$dealId")({
  validateSearch: (search: Record<string, unknown>): DealDetailSearch => {
    const n = search.note;
    return typeof n === "string" ? { note: n } : {};
  },
  component: DealDetailPage,
});

function DealDetailPage() {
  const { dealId } = Route.useParams();
  const { note: highlightNoteId } = useSearch({ from: "/_app/deals/$dealId" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  // Default to view mode. The "Edit" button on the view card flips this on;
  // Save (success) and Cancel both flip it back off.
  const [editing, setEditing] = useState(false);

  const {
    data: deal,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["deal", dealId],
    queryFn: () => dealsApi.get(dealId),
  });

  if (isLoading) {
    return (
      <Shell>
        <p className="text-sm opacity-60">Loading…</p>
      </Shell>
    );
  }

  if (error) {
    const status = error instanceof ApiError ? error.status : 0;
    return (
      <Shell>
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {status === 404
            ? "This deal doesn't exist or you don't have access to it."
            : `Couldn't load deal: ${(error as Error).message}`}
        </div>
      </Shell>
    );
  }

  // No data yet, but neither the loading nor error branch above matched —
  // this is react-query's "paused" state (offline / flaky network on the
  // field PWA). Show a placeholder instead of a blank screen.
  if (!deal || !user) {
    return (
      <Shell>
        <p className="text-sm opacity-60">Loading…</p>
      </Shell>
    );
  }

  const owns = deal.assigned_to_id === user.id || deal.created_by_id === user.id;
  const isPrivileged = user.role === "admin" || user.role === "manager";
  const canEditBasics = isPrivileged || owns;
  const canEditAdvanced = isPrivileged;
  const canEdit = canEditBasics || canEditAdvanced;
  const canDelete = user.role === "admin";

  return (
    <Shell>
      {editing ? (
        <DealEditor
          deal={deal}
          canEditBasics={canEditBasics}
          canEditAdvanced={canEditAdvanced}
          onSaved={(updated) => {
            queryClient.setQueryData(["deal", dealId], updated);
            queryClient.invalidateQueries({ queryKey: ["deals"] });
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <DealView deal={deal} canEdit={canEdit} onEdit={() => setEditing(true)} />
      )}
      <FollowUpsPanel
        parentType="deal"
        parentId={deal.id}
        defaultAssignedToId={deal.assigned_to_id}
      />
      <DispatchesPanel parentType="deal" parentId={deal.id} />
      <ConvertToOrderCard deal={deal} />
      <CustomFieldsPanel entity="deals" recordKey={deal.id} />
      <ChatterPanel
        parentType="deal"
        parentId={deal.id}
        highlightNoteId={highlightNoteId ?? null}
      />{" "}
      {canDelete && (
        <DeleteCard
          deal={deal}
          onDeleted={() => {
            queryClient.removeQueries({ queryKey: ["deal", dealId] });
            queryClient.invalidateQueries({ queryKey: ["deals"] });
            queryClient.invalidateQueries({ queryKey: ["deals-trash"] });
            navigate({ to: "/deals" });
          }}
        />
      )}
    </Shell>
  );
}

// ── Convert to order ─────────────────────────────────────────────────────────

function ConvertToOrderCard({ deal }: { deal: Deal }) {
  return (
    <section className="rounded-xl border border-[var(--border)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-0.5">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <ShoppingCart className="size-4 opacity-70" aria-hidden />
            Order
          </h2>
          <p className="text-xs opacity-60">
            Turn this deal into an ERP order, then add it to a dispatch when it's ready.
          </p>
        </div>
        <Link
          to="/erp/orders/new"
          search={{
            deal_id: deal.id,
            account_id: deal.account_id ?? undefined,
            contact_id: deal.contact_id ?? undefined,
          }}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          <ShoppingCart className="size-4" aria-hidden /> Convert to order
        </Link>
      </div>
    </section>
  );
}

// ── View mode ───────────────────────────────────────────────────────────────

function DealView({
  deal,
  canEdit,
  onEdit,
}: {
  deal: Deal;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const hasContact = deal.contact_id != null;
  const hasAccount = deal.account_id != null;

  // Pull contact / account + (optionally) the assignee names so the
  // view card doesn't render a wall of UUIDs. These queries are
  // TanStack-cached so the toggle into edit mode and back doesn't
  // re-fetch. The contact query only runs when the deal carries one.
  const contactQ = useQuery({
    queryKey: ["contact", deal.contact_id],
    queryFn: () => contactsApi.get(deal.contact_id as string),
    enabled: hasContact,
  });
  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200, offset: 0 }],
    queryFn: () => accountsApi.list({ limit: 200, offset: 0 }),
  });
  const usersQ = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
  });

  const accountName =
    deal.account_id && accountsQ.data?.items.find((a) => a.id === deal.account_id)?.name;
  const assignee =
    deal.assigned_to_id && usersQ.data?.items.find((u) => u.id === deal.assigned_to_id);

  return (
    <section className="space-y-5 rounded-xl border border-[var(--border)] p-5">
      {/* Display rule: contact wins for the primary header card; the
          account is shown as a secondary line in the contact card when
          both are set. Account-only deals get their own header. */}
      {hasContact ? (
        <ContactParentHeader
          contactId={deal.contact_id as string}
          contactName={contactQ.data?.name ?? null}
          contactPhone={contactQ.data?.phone ?? null}
          accountId={deal.account_id}
          accountName={accountName ?? null}
        />
      ) : (
        // hasAccount must be true here — at-least-one is enforced
        // server-side, so this branch is the account-only deal.
        <AccountParentHeader
          accountId={deal.account_id as string}
          accountName={accountName ?? null}
        />
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <DealStagePill stage={deal.stage} />
        <DealStatusPill status={deal.status} />
        <DealValueChip value={deal.value} />
        {deal.closed_at && (
          <span className="text-xs opacity-60">
            · Closed {new Date(deal.closed_at).toLocaleDateString()}
          </span>
        )}
      </div>

      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
        <ViewRow label="Stage" value={stageLabel(deal.stage)} />
        <ViewRow label="Status" value={statusLabel(deal.status)} />
        <ViewRow label="Value" value={valueLabel(deal.value)} />
        {/* Show the account row when the deal has BOTH parents. For
            account-only deals the header already names the account, so
            the row would be redundant. */}
        {hasContact && (
          <ViewRow
            label="Account"
            value={
              accountName ??
              (hasAccount ? (
                `${(deal.account_id as string).slice(0, 8)}…`
              ) : (
                <em className="opacity-50">(no account)</em>
              ))
            }
          />
        )}
        <ViewRow
          label="Assigned to"
          value={
            assignee ? (
              <span>
                {assignee.name || assignee.email}{" "}
                <span className="text-xs opacity-60">— {assignee.role}</span>
              </span>
            ) : deal.assigned_to_id ? (
              <span className="font-mono text-xs">{deal.assigned_to_id.slice(0, 8)}…</span>
            ) : (
              <em className="opacity-50">(unassigned)</em>
            )
          }
        />
      </dl>

      <ViewBlock label="Requirements" value={deal.requirements} />
      <ViewBlock label="Internal notes" value={deal.notes} />

      <Meta deal={deal} />

      {canEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-2 rounded-md border border-[var(--border)] px-4 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5"
        >
          <Pencil className="size-4" aria-hidden />
          Edit
        </button>
      ) : (
        <p className="text-xs opacity-60">You don't have permission to edit this deal.</p>
      )}
    </section>
  );
}

/** Header card rendered above a deal with a contact parent. When an
 *  account is also attached, it shows up as a secondary line + chip
 *  (the v1.2 "this person at this company" common case). */
function ContactParentHeader({
  contactId,
  contactName,
  contactPhone,
  accountId,
  accountName,
}: {
  contactId: string;
  contactName: string | null;
  contactPhone: string | null;
  /** null when the deal has no account attached. */
  accountId: string | null;
  /** null when the lookup is still loading or the account isn't visible. */
  accountName: string | null;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[var(--border)] bg-black/5 p-3 dark:bg-white/5">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide opacity-50">Contact</div>
        <div className="truncate text-sm font-medium">
          {contactName ?? `Contact ${contactId.slice(0, 8)}…`}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs opacity-60">
          {contactPhone && <span>{contactPhone}</span>}
          {accountId && (
            <Link
              to="/accounts/$accountId"
              params={{ accountId }}
              className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 hover:underline dark:bg-indigo-900/40 dark:text-indigo-200"
            >
              @ {accountName ?? `${accountId.slice(0, 8)}…`}
            </Link>
          )}
        </div>
      </div>
      <Link
        to="/contacts/$contactId"
        params={{ contactId }}
        className="text-sm font-medium text-[var(--accent)] hover:underline"
      >
        Open contact →
      </Link>
    </div>
  );
}

/** Header card rendered above a deal that only has an account parent. */
function AccountParentHeader({
  accountId,
  accountName,
}: {
  accountId: string;
  accountName: string | null;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[var(--border)] bg-black/5 p-3 dark:bg-white/5">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide opacity-50">Account</div>
        <div className="truncate text-sm font-medium">
          {accountName ?? `Account ${accountId.slice(0, 8)}…`}
        </div>
        <div className="text-xs opacity-60">
          No contact attached — feel free to link one once you know who at the company is involved.
        </div>
      </div>
      <Link
        to="/accounts/$accountId"
        params={{ accountId }}
        className="text-sm font-medium text-[var(--accent)] hover:underline"
      >
        Open account →
      </Link>
    </div>
  );
}

function ViewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide opacity-50">{label}</dt>
      <dd className="mt-0.5 truncate">{value}</dd>
    </div>
  );
}

function ViewBlock({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide opacity-50">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap text-sm">
        {value ? value : <em className="opacity-50">—</em>}
      </dd>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <BackLink fallbackTo="/deals" fallbackLabel="deals" />
        <h1 className="text-2xl font-semibold tracking-tight">Deal</h1>
      </header>
      {children}
    </div>
  );
}

// ── Edit form ───────────────────────────────────────────────────────────────

interface EditorProps {
  deal: Deal;
  canEditBasics: boolean;
  canEditAdvanced: boolean;
  onSaved: (l: Deal) => void;
  /** Cancel out of edit mode and return to view. The form discards any
   * unsaved local state implicitly because it gets unmounted. */
  onCancel: () => void;
}

function DealEditor({ deal, canEditBasics, canEditAdvanced, onSaved, onCancel }: EditorProps) {
  const queryClient = useQueryClient();
  const hasContact = deal.contact_id != null;

  const [stage, setStage] = useState<string>(deal.stage);
  const [status, setStatus] = useState<string>(deal.status);
  const [value, setValue] = useState<string>(deal.value);

  // Live picklists. Loaded once per detail render; the deal-detail
  // page is itself low-traffic so we don't bother with an upstream
  // shared cache.
  const stageOptionsQ = usePicklistOptions("deal_stage");
  const statusOptionsQ = usePicklistOptions("deal_status");
  const valueOptionsQ = usePicklistOptions("deal_value");
  const terminalStages = usePicklistTerminalSet("deal_stage").data;
  const [requirements, setRequirements] = useState(deal.requirements ?? "");
  const [notes, setNotes] = useState(deal.notes ?? "");
  const [accountId, setAccountId] = useState(deal.account_id ?? "");
  const [assigneeId, setAssigneeId] = useState(deal.assigned_to_id ?? "");
  const [error, setError] = useState<string | null>(null);

  // Contact context — needed to render the header link and resolve the
  // contact's name/phone. Only fetched when the deal carries a contact.
  const contactQ = useQuery({
    queryKey: ["contact", deal.contact_id],
    queryFn: () => contactsApi.get(deal.contact_id as string),
    enabled: hasContact,
  });
  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200, offset: 0 }],
    queryFn: () => accountsApi.list({ limit: 200, offset: 0 }),
    enabled: canEditAdvanced,
  });
  const usersQ = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: canEditAdvanced,
  });

  useEffect(() => {
    setStage(deal.stage);
    setStatus(deal.status);
    setValue(deal.value);
    setRequirements(deal.requirements ?? "");
    setNotes(deal.notes ?? "");
    setAccountId(deal.account_id ?? "");
    setAssigneeId(deal.assigned_to_id ?? "");
  }, [deal]);

  const updateMutation = useMutation({
    mutationFn: ({ body, share }: { body: DealUpdate; share?: boolean }) =>
      dealsApi.update(deal.id, body, { share: share ?? false }),
    onSuccess: (updated) => {
      onSaved(updated);
      // Reopen and close both flip closed_at, so the contact's deal list and
      // the deals table need to refetch.
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      setError(null);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const body: DealUpdate = {};
    if (canEditBasics) {
      if (stage !== deal.stage) body.stage = stage;
      if (status !== deal.status) body.status = status;
      if (value !== deal.value) body.value = value;
      const reqNorm = requirements.trim() || null;
      if (reqNorm !== deal.requirements) body.requirements = reqNorm;
      const notesNorm = notes.trim() || null;
      if (notesNorm !== deal.notes) body.notes = notesNorm;
    }
    if (canEditAdvanced) {
      const acctNorm = accountId.trim() || null;
      // Server enforces "deal needs at least one parent" — pre-empt
      // the 422 here so the rep gets a friendly inline message instead
      // of a backend error toast. (PATCH can't change contact_id, so a
      // contact-less deal trying to drop its account is the only way
      // to land in the "no parent left" state from this form.)
      if (acctNorm === null && !hasContact) {
        setError("This deal has no contact, so it must keep an account attached.");
        return;
      }
      if (acctNorm !== deal.account_id) body.account_id = acctNorm;
      const asgNorm = assigneeId.trim() || null;
      if (asgNorm !== deal.assigned_to_id) body.assigned_to_id = asgNorm;
    }

    if (Object.keys(body).length === 0) {
      setError("Nothing to save — no fields have changed.");
      return;
    }

    updateMutation.mutate(
      { body },
      {
        onError: (err) => {
          // 422 contact_account_mismatch fires when the rep picks an
          // account on a contact-bearing deal but the contact doesn't
          // belong to that account. Surface the actual contact account
          // so they can self-correct.
          if (err instanceof ApiError && isDealConflictDetail(err.detail)) {
            if (err.status === 422 && err.detail.code === "contact_account_mismatch") {
              setError(err.detail.message);
              return;
            }
            // 409 assignee_lacks_account_access: reassigning to a rep who
            // can't see the parent account. Ask, then retry granting them
            // access as part of the reassignment.
            if (err.status === 409 && err.detail.code === "assignee_lacks_account_access") {
              if (window.confirm(err.detail.message)) {
                updateMutation.mutate(
                  { body, share: true },
                  {
                    onError: (retryErr) => {
                      const m =
                        retryErr instanceof ApiError ? retryErr.message : "Could not save changes.";
                      setError(m);
                    },
                  },
                );
              }
              return;
            }
          }
          const msg = err instanceof ApiError ? err.message : "Could not save changes.";
          setError(msg);
        },
      },
    );
  };

  const submitting = updateMutation.isPending;
  const allReadOnly = !canEditBasics && !canEditAdvanced;

  // Picklist-driven terminal check. Falls back to "no terminal" while
  // the picklist loads — saves us flashing a "will close" hint based
  // on stale seed data; the worst case is a one-frame delay before the
  // hint appears (the user can't submit yet anyway, the stage button
  // they just clicked is still highlighted).
  const wantsToClose = terminalStages.has(stage) && !terminalStages.has(deal.stage);
  const wantsToReopen = !terminalStages.has(stage) && terminalStages.has(deal.stage);
  const accountSnapshotName =
    deal.account_id && accountsQ.data?.items.find((a) => a.id === deal.account_id)?.name;

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-5 rounded-xl border border-[var(--border)] p-5"
      noValidate
    >
      {/* Parent summary header — quick context + jump back. */}
      {hasContact ? (
        <ContactParentHeader
          contactId={deal.contact_id as string}
          contactName={contactQ.data?.name ?? null}
          contactPhone={contactQ.data?.phone ?? null}
          accountId={deal.account_id}
          accountName={accountSnapshotName ?? null}
        />
      ) : (
        <AccountParentHeader
          accountId={deal.account_id as string}
          accountName={accountSnapshotName ?? null}
        />
      )}

      {/* Current snapshot pills */}
      <div className="flex flex-wrap items-center gap-1.5">
        <DealStagePill stage={deal.stage} />
        <DealStatusPill status={deal.status} />
        <DealValueChip value={deal.value} />
        {deal.closed_at && (
          <span className="text-xs opacity-60">
            · Closed {new Date(deal.closed_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Stage picker — full width grid of buttons. Terminal buttons are
          highlighted as "close" actions; non-terminal ones are normal.
          Picklist-driven: manager-added stages render alongside the
          seeds in their configured order. */}
      <Field id="deal-stage" label="Stage">
        <fieldset className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <legend className="sr-only">Deal stage</legend>
          {stageOptionsQ.data.map((opt) => {
            const s = opt.value;
            const selected = stage === s;
            const terminal = opt.is_terminal;
            const id = `deal-stage-${s}`;
            return (
              <label
                key={opt.id}
                htmlFor={id}
                className={`cursor-pointer rounded-md border px-3 py-2 text-center text-sm transition-colors ${
                  !canEditBasics ? "cursor-not-allowed opacity-50" : ""
                } ${
                  selected
                    ? terminal
                      ? "border-emerald-500 bg-emerald-500/10 font-medium"
                      : "border-[var(--accent)] bg-[var(--accent)]/10 font-medium"
                    : "border-[var(--border)] hover:bg-black/5 dark:hover:bg-white/5"
                }`}
              >
                <input
                  id={id}
                  type="radio"
                  name="deal-stage"
                  value={s}
                  checked={selected}
                  disabled={!canEditBasics}
                  onChange={() => setStage(s)}
                  className="sr-only"
                />
                {terminal &&
                  (s === "won" ? (
                    <CheckCircle2 className="-mt-0.5 mr-1 inline size-4" aria-hidden />
                  ) : (
                    <XCircle className="-mt-0.5 mr-1 inline size-4" aria-hidden />
                  ))}
                {opt.label}
              </label>
            );
          })}
        </fieldset>
        {wantsToClose && (
          <p className="mt-2 text-xs opacity-70">
            Saving will <strong>close</strong> this deal.
          </p>
        )}
        {wantsToReopen && (
          <p className="mt-2 text-xs opacity-70">
            Saving will <strong>reopen</strong> this deal and clear its closed-at timestamp.
          </p>
        )}
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="deal-status" label="Status">
          <select
            id="deal-status"
            value={status}
            disabled={!canEditBasics}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
          >
            {/* Include the deal's *current* status even if it's been
                deactivated since (so the form stays consistent with
                what's saved). The active set comes from the picklist. */}
            {dedupeStatusOptions(statusOptionsQ.data, deal.status).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>

        <Field id="deal-value" label="Deal value">
          <select
            id="deal-value"
            value={value}
            disabled={!canEditBasics}
            onChange={(e) => setValue(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
          >
            {dedupeStatusOptions(valueOptionsQ.data, deal.value).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field id="deal-requirements" label="Requirements">
        <textarea
          id="deal-requirements"
          rows={3}
          value={requirements}
          disabled={!canEditBasics}
          onChange={(e) => setRequirements(e.target.value)}
          className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
        />
      </Field>

      <Field id="deal-notes" label="Internal notes">
        <textarea
          id="deal-notes"
          rows={2}
          value={notes}
          disabled={!canEditBasics}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
        />
      </Field>

      <Field
        id="deal-account"
        label="Account snapshot"
        hint={
          canEditAdvanced
            ? "Manager/Admin only. Defaults to the contact's account at create time; can drift."
            : "Read-only — only managers and admins can change this."
        }
      >
        {canEditAdvanced ? (
          <select
            id="deal-account"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          >
            <option value="">(no account)</option>
            {accountsQ.data?.items.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
            {accountId && !accountsQ.data?.items.some((a) => a.id === accountId) && (
              <option value={accountId}>(unknown — {accountId.slice(0, 8)}…)</option>
            )}
          </select>
        ) : (
          <input
            id="deal-account"
            type="text"
            value={accountId || "(no account)"}
            disabled
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm opacity-60"
          />
        )}
      </Field>

      <Field
        id="deal-assignee"
        label="Assigned to"
        hint={
          canEditAdvanced
            ? "Manager/Admin only. Sales can only own deals assigned to themselves."
            : "Read-only — only managers and admins can change this."
        }
      >
        {canEditAdvanced ? (
          <select
            id="deal-assignee"
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          >
            <option value="">(unassigned)</option>
            {usersQ.data?.items.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email} — {u.role}
              </option>
            ))}
            {assigneeId && !usersQ.data?.items.some((u) => u.id === assigneeId) && (
              <option value={assigneeId}>(unknown — {assigneeId.slice(0, 8)}…)</option>
            )}
          </select>
        ) : (
          <input
            id="deal-assignee"
            type="text"
            value={assigneeId || "(unassigned)"}
            disabled
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm opacity-60"
          />
        )}
      </Field>

      <Meta deal={deal} />

      {error && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {!allReadOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Saving…
              </>
            ) : wantsToReopen ? (
              <>
                <RotateCcw className="size-4" aria-hidden />
                Save (reopen)
              </>
            ) : wantsToClose ? (
              <>
                <CheckCircle2 className="size-4" aria-hidden />
                Save (close)
              </>
            ) : (
              <>
                <Save className="size-4" aria-hidden />
                Save changes
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-md border border-[var(--border)] px-4 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}

      {allReadOnly && (
        <p className="text-xs opacity-60">You don't have permission to edit this deal.</p>
      )}
    </form>
  );
}

// ── Soft-delete (Admin only) ────────────────────────────────────────────────

function DeleteCard({ deal, onDeleted }: { deal: Deal; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => dealsApi.remove(deal.id),
    onSuccess: () => onDeleted(),
    onError: (err) => {
      if (err instanceof ApiError && err.reauthRequired) {
        setError("For security, please log out and log back in to confirm this delete.");
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not delete deal.";
      setError(msg);
    },
  });

  return (
    <div className="space-y-3 rounded-xl border border-red-300/60 p-5 dark:border-red-900/60">
      <div className="flex items-start gap-3">
        <Trash2 className="mt-0.5 size-4 text-red-600 dark:text-red-400" aria-hidden />
        <div>
          <h2 className="text-sm font-semibold text-red-700 dark:text-red-300">Danger zone</h2>
          <p className="text-xs opacity-70">
            Soft-deleting hides this deal from all lists. The "one open deal per contact" slot
            becomes available again. Admin can restore from <em>Trash</em>.
          </p>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {!confirming ? (
        <button
          type="button"
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
          className="rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40"
        >
          Move to trash…
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">Move this deal to trash?</span>
          <button
            type="button"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {deleteMutation.isPending ? "Deleting…" : "Confirm delete"}
          </button>
          <button
            type="button"
            disabled={deleteMutation.isPending}
            onClick={() => setConfirming(false)}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Small bits ──────────────────────────────────────────────────────────────

function Meta({ deal }: { deal: Deal }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-[var(--border)] pt-3 text-xs opacity-60">
      <dt>Created</dt>
      <dd>{new Date(deal.created_at).toLocaleString()}</dd>
      <dt>Updated</dt>
      <dd>{new Date(deal.updated_at).toLocaleString()}</dd>
      {deal.closed_at && (
        <>
          <dt>Closed</dt>
          <dd>{new Date(deal.closed_at).toLocaleString()}</dd>
        </>
      )}
    </dl>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="text-sm">
      <label htmlFor={id} className="mb-1 block opacity-80">
        {label}
      </label>
      {children}
      {hint && <span className="mt-1 block text-xs opacity-50">{hint}</span>}
    </div>
  );
}

/**
 * Append the deal's currently-saved value to the picklist's active set
 * if it isn't already there.
 *
 * The "current value" might be a row a manager has since deactivated.
 * Without this fallback, the form would jump to the first active
 * option on every render — tossing the saved value into the void
 * before the user even notices. Prepending the inactive row keeps the
 * select consistent with what's stored.
 */
function dedupeStatusOptions(
  options: { id: string; value: string; label: string }[],
  currentValue: string,
): { id: string; value: string; label: string }[] {
  if (options.some((o) => o.value === currentValue)) return options;
  return [
    { id: `__current__:${currentValue}`, value: currentValue, label: currentValue },
    ...options,
  ];
}
